import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Calendar, 
  FileText, 
  MessageCircle, 
  CheckCircle,
  DollarSign,
  User,
  Clock,
  BarChart3,
  Bell,
  GraduationCap,
  Eye
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import TeacherLayout from '../../components/layout/TeacherLayout';
import TeacherQuickActions from '../../components/teacher/TeacherQuickActions';
import { getMasterTimetable } from '../../firebase/collections';

import TeacherAttendanceStatus from '../../components/teacher/TeacherAttendanceStatus';
import teacherBatchSync from '../../services/teacherBatchSync';
import { getBatchEnrollments } from '../../firebase/collections';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

const PERIODS = [
  'Period 1 (9:00-9:45)',
  'Period 2 (9:45-10:30)',
  'Period 3 (10:30-11:15)',
  'Period 4 (11:15-12:00)',
  'Break (12:00-12:30)',
  'Period 5 (12:30-1:15)',
  'Period 6 (1:15-2:00)',
  'Period 7 (2:00-2:45)',
  'Period 8 (2:45-3:30)'
];

const TeacherDashboard = () => {
  const { currentUser, userProfile } = useRealtime();
  const [stats, setStats] = useState({
    totalBatches: 0,
    totalStudents: 0,
    pendingDoubts: 0,
    upcomingClasses: 0,
    monthlyAttendance: 0,
    pendingLeaves: 0
  });
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [periods, setPeriods] = useState(PERIODS);

  useEffect(() => {
    if (currentUser?.uid && userProfile?.role === 'teacher') {
      loadTeacherData();
      
      // Subscribe to real-time batch updates
      const unsubscribe = teacherBatchSync.subscribeToTeacherBatches(
        currentUser.uid,
        (updatedBatches) => {
          console.log('Real-time batch update received in dashboard:', updatedBatches.length, 'batches');
          setBatches(updatedBatches);
          updateStats(updatedBatches);
        }
      );
      
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [currentUser, userProfile]);

  const loadTeacherData = async () => {
    try {
      setLoading(true);
      
      console.log('Loading teacher dashboard data for:', currentUser.uid);
      
      // Query batches where teacherIds array contains current user
      const batchesQuery = query(
        collection(db, 'batches'),
        where('teacherIds', 'array-contains', currentUser.uid)
      );
      
      const batchesSnapshot = await getDocs(batchesQuery);
      const batchesList = [];
      
      batchesSnapshot.forEach((doc) => {
        const batchData = doc.data();
        console.log('Found batch:', doc.id, batchData.name);
        batchesList.push({
          id: doc.id,
          ...batchData
        });
      });
      
      console.log(`Loaded ${batchesList.length} batches for teacher`);
      
      // Enrich batches with student counts
      const enrichedBatches = await Promise.all(
        batchesList.map(async (batch) => {
          try {
            const enrollmentsResult = await getBatchEnrollments(batch.id);
            const studentCount = enrollmentsResult.success && enrollmentsResult.data 
              ? enrollmentsResult.data.length 
              : 0;
            
            return {
              ...batch,
              studentCount,
              totalStudents: studentCount
            };
          } catch (error) {
            console.error(`Error getting enrollments for batch ${batch.id}:`, error);
            return {
              ...batch,
              studentCount: 0,
              totalStudents: 0
            };
          }
        })
      );
      
      console.log('Enriched batches with student counts:', enrichedBatches.map(b => ({
        id: b.id,
        name: b.name,
        studentCount: b.studentCount
      })));
      
      setBatches(enrichedBatches);
      await updateStats(enrichedBatches);
      
      // Load real recent activity
      const recentActivity = await teacherBatchSync.getTeacherRecentActivity(currentUser.uid, 5);
      setRecentActivity(recentActivity);
      
      // Load today's schedule
      const today = new Date().toLocaleLowerCase('en-US', { weekday: 'long' });
      const timetableResult = await getMasterTimetable(userProfile?.coachingId);
      if (timetableResult.success) {
        const data = timetableResult.data || {};
        const slots = Array.isArray(data) ? data : (data.slots || []);
        const loadedPeriods = Array.isArray(data) ? PERIODS : (data.periods && data.periods.length > 0 ? data.periods : PERIODS);
        setPeriods(loadedPeriods);

        const todaySlots = slots
          .filter((slot) => slot.day.toLowerCase() === today && slot.teacherId === currentUser.uid)
          .sort((a, b) => loadedPeriods.indexOf(a.period) - loadedPeriods.indexOf(b.period));
        setTodaySchedule(todaySlots);
      }
      
    } catch (error) {
      console.error('Error loading teacher dashboard data:', error);
      // Fallback to basic data on error
      const teacherBatchIds = userProfile?.teacherProfile?.batches || [];
      setBatches(teacherBatchIds);
      updateStats(teacherBatchIds);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = async (batchList) => {
    try {
      // Calculate total students across all batches using real enrollment data
      let totalStudents = 0;
      
      for (const batch of batchList) {
        if (batch.id) {
          try {
            // Use same logic as TeacherBatchManagement - try getBatchEnrollments first
            try {
              const enrollmentsResult = await getBatchEnrollments(batch.id);
              
              if (enrollmentsResult.success && enrollmentsResult.data && enrollmentsResult.data.length > 0) {
                const studentCount = enrollmentsResult.data.length;
                console.log(`📊 Batch ${batch.name || batch.id} has ${studentCount} enrolled students (from getBatchEnrollments)`);
                totalStudents += studentCount;
              } else {
                // Fallback to teacherBatchSync method
                const studentsFromSync = await teacherBatchSync.getBatchStudents(batch.id);
                const studentCount = studentsFromSync.length;
                console.log(`📊 Batch ${batch.name || batch.id} has ${studentCount} students (from getBatchStudents fallback)`);
                totalStudents += studentCount;
              }
            } catch (enrollmentError) {
              console.log(`🔍 Error with getBatchEnrollments for batch ${batch.id}, trying alternatives...`);
              
              // Final fallback to batch properties
              if (batch.students && Array.isArray(batch.students) && batch.students.length > 0) {
                console.log(`📋 Found ${batch.students.length} students in batch.students array`);
                totalStudents += batch.students.length;
              } else if (batch.studentCount) {
                console.log(`📋 Found studentCount property: ${batch.studentCount}`);
                totalStudents += batch.studentCount;
              } else if (batch.totalStudents) {
                console.log(`📋 Found totalStudents property: ${batch.totalStudents}`);
                totalStudents += batch.totalStudents;
              } else {
                console.log(`⚠️ No student data found for batch ${batch.id}`);
              }
            }
          } catch (error) {
            console.error('Error getting student count for batch:', batch.id, error);
            // Fallback to batch data if available
            const fallbackCount = batch.studentCount || batch.totalStudents || batch.students?.length || 0;
            console.log(`Using fallback count for batch ${batch.name || batch.id}: ${fallbackCount}`);
            totalStudents += fallbackCount;
          }
        }
      }
      
      console.log(`Total students across all ${batchList.length} batches: ${totalStudents}`);
      
      // Load real stats from all batches
      let totalPendingDoubts = 0;
      let totalUpcomingClasses = 0;
      let totalAttendanceSum = 0;
      let batchesWithAttendance = 0;
      
      for (const batch of batchList) {
        if (batch.id) {
          try {
            const [pendingDoubts, upcomingClasses, attendanceRate] = await Promise.all([
              teacherBatchSync.getBatchPendingDoubts(batch.id),
              teacherBatchSync.getBatchUpcomingClasses(batch.id),
              teacherBatchSync.getBatchAttendanceRate(batch.id)
            ]);
            
            totalPendingDoubts += pendingDoubts.length;
            totalUpcomingClasses += upcomingClasses.length;
            
            if (attendanceRate > 0) {
              totalAttendanceSum += attendanceRate;
              batchesWithAttendance++;
            }
          } catch (error) {
            // Error loading stats for batch
          }
        }
      }
      
      const averageAttendance = batchesWithAttendance > 0 ? Math.round(totalAttendanceSum / batchesWithAttendance) : 0;
      
      setStats({
        totalBatches: batchList.length,
        totalStudents,
        pendingDoubts: totalPendingDoubts,
        upcomingClasses: totalUpcomingClasses,
        monthlyAttendance: averageAttendance,
        pendingLeaves: 0 // This would come from leave management system when implemented
      });
    } catch (error) {
      // Fallback to basic stats
      const fallbackStudents = batchList.reduce((sum, batch) => {
        return sum + (batch.studentCount || batch.totalStudents || batch.students?.length || 0);
      }, 0);
      
      setStats({
        totalBatches: batchList.length,
        totalStudents: fallbackStudents,
        pendingDoubts: 0,
        upcomingClasses: 0,
        monthlyAttendance: 0,
        pendingLeaves: 0
      });
    }
  };

  const teacherSections = [
    {
      title: 'My Profile',
      description: 'View and edit your profile',
      icon: User,
      link: '/teacher/profile',
      color: 'blue'
    },
    {
      title: 'My Attendance',
      description: 'View your attendance record',
      icon: Clock,
      link: '/teacher/my-attendance',
      color: 'green'
    },
    {
      title: 'Salary Details',
      description: 'View salary and payments',
      icon: DollarSign,
      link: '/teacher/salary',
      color: 'yellow'
    },
    {
      title: 'Leave Management',
      description: 'Apply for leave',
      icon: Calendar,
      link: '/teacher/leave',
      color: 'purple'
    },
    {
      title: 'Settlements',
      description: 'View payment settlements',
      icon: BarChart3,
      link: '/teacher/settlements',
      color: 'indigo'
    },
    {
      title: 'Invoices',
      description: 'Download invoices',
      icon: FileText,
      link: '/teacher/invoices',
      color: 'red'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30',
      green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30',
      yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
      purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30',
      indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/30',
      red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <TeacherLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  return (
    <TeacherLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-gray-900 dark:text-white mb-2"
          >
            Welcome, {currentUser?.displayName || userProfile?.displayName || 'Teacher'}! 👨‍🏫
          </motion.h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your classes, students, and teaching activities
          </p>
        </div>



        {/* Quick Actions */}
        <div className="mb-8">
          <TeacherQuickActions />
        </div>

        {/* Today's Schedule */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center mb-6">
              <Calendar className="w-5 h-5 mr-2 text-green-600" />
              Today's Schedule
            </h2>
            
            {todaySchedule.length > 0 ? (
              <div className="space-y-3">
                {todaySchedule.map((slot, index) => {
                  const now = new Date();
                  const currentTime = now.getHours() * 60 + now.getMinutes();
                  const periodTimes = {
                    'Period 1 (9:00-9:45)': { start: 9*60, end: 9*60+45 },
                    'Period 2 (9:45-10:30)': { start: 9*60+45, end: 10*60+30 },
                    'Period 3 (10:30-11:15)': { start: 10*60+30, end: 11*60+15 },
                    'Period 4 (11:15-12:00)': { start: 11*60+15, end: 12*60 },
                    'Period 5 (12:30-1:15)': { start: 12*60+30, end: 13*60+15 },
                    'Period 6 (1:15-2:00)': { start: 13*60+15, end: 14*60 },
                    'Period 7 (2:00-2:45)': { start: 14*60, end: 14*60+45 },
                    'Period 8 (2:45-3:30)': { start: 14*60+45, end: 15*60+30 }
                  };
                  
                  const periodTime = periodTimes[slot.period];
                  const isCurrent = periodTime && currentTime >= periodTime.start && currentTime <= periodTime.end;
                  const isUpcoming = periodTime && currentTime < periodTime.start;
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`p-4 rounded-lg border ${
                        isCurrent 
                          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700' 
                          : isUpcoming 
                            ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'
                            : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Clock className={`w-5 h-5 ${isCurrent ? 'text-green-600' : isUpcoming ? 'text-blue-600' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{slot.period}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {slot.subject} - {batches.find(b => b.id === slot.classId)?.name || 'Unknown Class'}
                              {slot.room && ` (Room ${slot.room})`}
                            </p>
                          </div>
                        </div>
                        {isCurrent && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            Current
                          </span>
                        )}
                        {isUpcoming && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                            Upcoming
                          </span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No classes scheduled for today</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* My Batches */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                  My Batches
                </h2>
                <Link
                  to="/teacher/batches"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View All
                </Link>
              </div>
              
              {batches.length > 0 ? (
                <div className="space-y-4">
                  {batches.slice(0, 3).map((batch, index) => (
                    <motion.div
                      key={batch.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {batch.name || `Batch ${index + 1}`}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {batch.studentCount || batch.totalStudents || 0} students • {batch.subject || 'Subject not specified'}
                          </p>
                        </div>
                      </div>
                      <Link
                        to={`/teacher/batch/${batch.id || index}`}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Manage
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <GraduationCap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Batches Assigned
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    You haven't been assigned to any batches yet. Contact your administrator.
                  </p>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Request Batch Assignment
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <Bell className="w-5 h-5 mr-2 text-yellow-600" />
                Recent Activity
              </h2>
              
              <div className="space-y-4">
                {recentActivity.length > 0 ? (
                  recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activity.type === 'assignment' ? 'bg-blue-100 text-blue-600' :
                        activity.type === 'doubt' ? 'bg-purple-100 text-purple-600' :
                        'bg-green-100 text-green-600'
                      }`}>
                        {activity.type === 'assignment' ? <FileText className="w-4 h-4" /> :
                         activity.type === 'doubt' ? <MessageCircle className="w-4 h-4" /> :
                         <CheckCircle className="w-4 h-4" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {activity.message}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {activity.batch} • {activity.time}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No recent activity
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>



        {/* Attendance Status */}
        <div className="mb-8">
          <TeacherAttendanceStatus />
        </div>

        {/* Teacher Portal Sections */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Teacher Portal</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teacherSections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Link
                  to={section.link}
                  className={`block rounded-lg shadow-sm p-6 hover:shadow-md transition-all border ${getColorClasses(section.color)}`}
                >
                  <div className="flex items-center mb-3">
                    <section.icon className={`w-6 h-6 mr-3 ${
                      section.color === 'blue' ? 'text-blue-600' :
                      section.color === 'green' ? 'text-green-600' :
                      section.color === 'yellow' ? 'text-yellow-600' :
                      section.color === 'purple' ? 'text-purple-600' :
                      section.color === 'indigo' ? 'text-indigo-600' :
                      'text-red-600'
                    }`} />
                    <h3 className="font-semibold text-gray-900 dark:text-white">{section.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{section.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </TeacherLayout>
  );
};

export default TeacherDashboard;